/**
 * Downsizer / Downsizing MODULE scenario + suggestion logic.
 *
 * Reuses the validated downsize mutator (applyDownsizeToFactFind: new home purchase, downsizer-to-super,
 * gifting, debt repayment, residual to bank) and the shared projection engine, so the module's numbers
 * match the sell-home paths already used across Aged Care / Support at Home / Retirement Village.
 *
 * Domain notes (current law, verified Jun 2026; surfaced as advice in the UI):
 *  - Downsizer super contribution: 55+, up to $300k per person ($600k couple) from a main residence owned
 *    10+ years, within 90 days of settlement; doesn't count to contribution caps. (DOWNSIZER_CONTRIBUTION_CAP)
 *  - Selling an exempt home converts equity into assessable assets — the Age-Pension "downsizing trap".
 *  - Home-sale proceeds you intend to use for a new home are EXEMPT from the assets test for up to 24 months
 *    (extendable to 36), and deemed at the LOWER rate during that period. This is a TRANSITIONAL rule between
 *    selling and buying; the module models the post-downsize STEADY STATE (new home exempt, freed capital
 *    deployed) and explains the transitional exemption in the insight panel.
 */
import { applyDownsizeToFactFind } from '../supportAtHome/supportAtHomeFactFindMutators.tsx';
import { annuityPreferenceOf, allowsResidual, allowsGuaranteed, type AnnuityAppetite, type AnnuityResidual } from '../../../../shared/src/calculation/annuityPreference.ts';
import { buildRecommendedOptimalStrategies, type EvaluatedStrategy, type RecommendedStrategyEntry } from '../../strategy/supportAtHomeRecommendedStrategies.ts';
import { FUNERAL_BOND_LIMIT, GIFTING_LIMIT_ANNUAL } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { calculateAge, formatCurrency } from '../../utils/index.ts';
import { payableDebtsOf } from '../agedCare/DebtPayoffStrategy.tsx';
import * as calculationApi from '../../services/calculationApi.ts';
import { calculateStampDuty, purchaseWithinBudget, isStampDutyState } from '../../../../shared/src/calculation/stampDuty.ts';
import { ASSET_CLASS_ASSUMPTIONS } from '../../../../shared/src/calculation/investmentProfiles.ts';
import { getRecommendedEmergencyFund } from '../../utils/emergencyFund.ts';
import { drawFundingMap } from '../../strategy/fundingMechanisms.ts';
import { allocateDownsizerContribution, canCommenceAbpFromDownsizer } from '../../../../shared/src/calculation/downsizerContributionUtils.ts';
import { allocateNonConcessionalContribution, nonConcessionalCapacityForHousehold } from '../../../../shared/src/calculation/nonConcessionalContributions.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { DownsizeDetails as CanonicalDownsizeDetails } from '../../strategy/supportAtHomeStrategyFunding.ts';

/** A `calculateFinancials` / projection-year result — free-form calc-engine output (see calculationApi.ts). */
export type Financials = Record<string, unknown> & {
    netWorth?: number;
    cashFlow?: number;
    netCashflow?: number;
    socialSecurity?: number;
    socialSecurityBreakdown?: unknown;
    cashFlowBreakdown?: unknown;
};

export type ProjectionYear = Financials & { year?: number };

/**
 * The `details` bag consumed by applyDownsizeToFactFind (Manual Control inputs, or a suggestion-engine
 * candidate) — a partial view of the canonical DownsizeDetails (features/supportAtHome), since any one
 * candidate/manual-control snapshot only ever sets the fields relevant to it.
 */
export type DownsizeDetails = Partial<CanonicalDownsizeDetails>;

interface RunProjectionsOptions {
    /**
     * When the downsize happens, `YYYY-MM-DD`. With a date, the ENGINE executes the sale, purchase
     * and allocations on the day against live balances (`downsizeEventPlugin`) — so callers must pass
     * the ORIGINAL fact-find plus the details, not the pre-transformed one, or the downsize happens
     * twice. Blank keeps the long-standing behaviour: the caller pre-applies and the engine just runs.
     */
    downsizeDate?: string;
    downsizeDetails?: Record<string, unknown>;
    debtPayoff?: Record<string, unknown> | null;
    legacyYears?: number;
    inflationAssumptionPct?: number;
}

interface BuildSuggestionsOptions {
    /** The case's downsize date, threaded to the server so every candidate shares the manual run's timing. */
    downsizeDate?: string;
    /**
     * A provider's quoted annual annuity income, where the client has one.
     *
     * Threaded into every annuity candidate rather than applied to the winner afterwards: the rate is
     * what the decision turns on, and a real quote can change WHICH candidate wins — so a ranking
     * struck on our indicative rate would then be the wrong ranking (owner, 2026-09-01).
     */
    annuityIncomeOverride?: number | null;
    legacyYears?: number;
    currentInputs?: DownsizeDetails;
    /**
     * What the client actually intends to buy, where they know.
     *
     * WHY THIS EXISTS (owner, 2026-08-28). The engine assumed a downsize always means buying
     * CHEAPER — it modelled one new home at 70% of the sale price and nothing else. But the reason
     * most people move is that the house has become too much to maintain, not that they want to
     * bank the difference. A low-maintenance apartment in the same suburb can cost the same as the
     * house, or more.
     *
     * That is not a detail. Buying at or above the sale price moves counted savings INTO an exempt
     * home, which pushes the Age Pension the OPPOSITE way to the downsizing trap this module warns
     * about — so the engine could not express the strategy that avoids the very problem it exists
     * to explain.
     *
     * Absent, the engine offers a spread of prices instead of guessing one.
     */
    intendedNewHomePrice?: number;
    /** The Strategy Analysis Setup answers (2 Oct 2026). Absent: buy at the anchor, a quarter, bills paid. */
    setup?: DzEngineSetup;
}

function zeroCareCosts(scenarioLivingExpenses: number) {
    return {
        totalDailyCost: 0, dailyAccommodationPayment: 0, basicDailyFee: 0,
        dailyNCCC: 0, dailyHSC: 0, dailyExtraServiceFee: 0,
        scenarioLivingExpenses,
    };
}

/** before/after financials for a downsize (the core of the Financial Impact panel). */
export async function getDownsizeBeforeAfter(
    factFind: DraftFactFind,
    details: DownsizeDetails,
): Promise<{ before: Financials; after: Financials; simulated: DraftFactFind }> {
    const before = (await calculationApi.calculateFinancials(factFind)) as Financials;
    const simulated = applyDownsizeToFactFind(factFind, details);
    const after = (await calculationApi.calculateFinancials(simulated)) as Financials;
    return { before, after, simulated };
}

/** Multi-year projection for the post-downsize position (reuses the shared engine with zero care costs). */
export async function runDownsizeProjections(
    simulatedFactFind: DraftFactFind,
    options: RunProjectionsOptions = {},
): Promise<ProjectionYear[]> {
    const legacyYears = options.legacyYears || 10;
    const scenarioLivingExpenses = parseFloat(String(simulatedFactFind?.expenses?.livingExpenses ?? '')) || 0;
    const result = await calculationApi.runProjections(
        simulatedFactFind,
        {
            scenarioLivingExpenses,
            ...(options.inflationAssumptionPct != null ? { inflationAssumptionPct: options.inflationAssumptionPct } : {}),
            // Dated: the engine sells, buys and allocates ON the day — see RunProjectionsOptions.
            ...(options.downsizeDate && options.downsizeDetails
                ? { downsizeEvent: { date: options.downsizeDate, details: options.downsizeDetails } }
                : {}),
            ...(options.debtPayoff ? { debtPayoff: options.debtPayoff } : {}),
        },
        zeroCareCosts(scenarioLivingExpenses),
        legacyYears,
    );
    return result as ProjectionYear[];
}

/*
 * ─── THE CANDIDATES ──────────────────────────────────────────────────────────────────────────
 *
 * REBUILT 2 Oct 2026 as LAYERED LEGS an actuary can follow, after the owner's review: the field
 * was a flat list that only ever BOUGHT a home (renting reached the engine as a nil price and
 * nothing else), put every freed dollar into an annuity, charged no duty on a bought investment
 * property, and let a price sensitivity win a goal. Now:
 *
 *   HOUSING   buy at the price the client named (or the 70% anchor), or sell and RENT, or both —
 *             from the Strategy Analysis Setup. Renting is a plan with a rent, a residency and
 *             Rent Assistance (downsizeEvent.ts), not the absence of a purchase.
 *   RESERVE   max(6 months' expenses, $50,000) stays in the bank before anything is deployed —
 *             the village and aged care rule (owner, 2 Oct 2026).
 *   LEGS      in a fixed order: super (the downsizer contribution — a qualifying SALE is the
 *             condition, never a purchase — as accumulation or a pension; after-tax where there is
 *             room) → the cheap means-test fixes (funeral bond, gift) → a lifetime annuity at the
 *             setup's share, never all of it → the remainder (bank, shares, property with its
 *             duty). Pure forms only: a 50/50 split lands between its parents on every goal and
 *             adds cards, not information (owner, 2 Oct 2026); mixing is Manual Control's.
 *   LADDER    the cheaper / similar / dearer prices are a SENSITIVITY, listed and explained but
 *             never crowned for a goal — a dearer home can win "Best Age Pension" by hiding more
 *             in the exempt home, which is true and is not advice (owner, 2 Oct 2026).
 */

/** "After the sale, will you buy or rent?" */
export type DzHousing = 'buy' | 'rent' | 'both';
/**
 * "Why are you thinking of moving?" (owner, 3 Oct 2026). The reason decides whether a dearer home
 * is a strategy or a what-if: a client moving to FREE UP MONEY does not want to be told to buy
 * dearer, so the price ladder is a sensitivity; a client moving for A HOME THAT SUITS THEM BETTER
 * can buy at or above the sale price, and moving counted savings into the exempt home is then a
 * real way to lift the Age Pension — the opposite of the downsizing trap — so those candidates
 * compete for the goals, with the cashflow-covered rule checking the bills still get paid.
 */
export type DzMotive = 'money' | 'home' | 'both';

export interface DzEngineSetup {
    /** Why they are moving. Default: both — every price competes. */
    motive?: DzMotive | null;
    /** Buy, rent, or compare both. Default: buy (rent where Manual Control already says so). */
    housing?: DzHousing | null;
    /** The new home's price where known; 0 = not sure, and the 70% anchor is used. */
    newHomePrice?: number | null;
    /** The state the new home (and any bought investment property) would be in — its duty scale. */
    newHomeState?: string | null;
    /** What they would pay a week if renting. */
    rentPerWeek?: number | null;
    /** The most of the deployable money a lifetime annuity may take, 0–100. Default a quarter. */
    annuityShareCapPct?: number | null;
    annuityAppetite?: AnnuityAppetite | null;
    annuityResidual?: AnnuityResidual | null;
    /** The yearly surplus a plan must keep for the cashflow-covered goal. Zero = the bills are paid. */
    cashflowTarget?: number | null;
}

/** A quarter by default — "all in" kept winning the cashflow goal on the income alone (the village's lesson, 24 Sep 2026). */
export const DEFAULT_DZ_ANNUITY_SHARE_CAP_PCT = 25;
/** The candidates that invest, buy or lock money away need this much deployable — the engine's own gate. */
export const DZ_SETUP_SPEND_THRESHOLD = 50_000;
/** The minimum credible lifetime annuity purchase. */
const DZ_ANNUITY_MIN = 25_000;

export function dzAnnuityShareCapOf(setup: DzEngineSetup | null | undefined): number {
    const raw = Number(setup?.annuityShareCapPct);
    if (!Number.isFinite(raw) || setup?.annuityShareCapPct == null) return DEFAULT_DZ_ANNUITY_SHARE_CAP_PCT;
    return Math.min(100, Math.max(0, raw));
}

/** The saved setup answers, with Manual Control's figures as the fallback for the housing ones. */
export function dzEngineSetupFromGoals(
    factFind: DraftFactFind | null | undefined,
    fallback: { housing?: DzHousing; newHomePrice?: number; newHomeState?: string; rentPerWeek?: number } = {},
): DzEngineSetup {
    const goals = (factFind?.goals || {}) as { dzMotive?: string; dzHousing?: string; dzNewHomePrice?: number; dzNewHomeState?: string; dzRentPerWeek?: number; dzAnnuityShareCapPct?: number; rvCashflowTarget?: number };
    const housing: DzHousing = goals.dzHousing === 'buy' || goals.dzHousing === 'rent' || goals.dzHousing === 'both' ? goals.dzHousing : (fallback.housing ?? 'buy');
    const motive: DzMotive = goals.dzMotive === 'money' || goals.dzMotive === 'home' || goals.dzMotive === 'both' ? goals.dzMotive : 'both';
    const preference = annuityPreferenceOf(factFind as never);
    return {
        motive,
        housing,
        newHomePrice: Number(goals.dzNewHomePrice) > 0 ? Number(goals.dzNewHomePrice) : (fallback.newHomePrice ?? 0),
        newHomeState: typeof goals.dzNewHomeState === 'string' && goals.dzNewHomeState.trim() !== '' ? goals.dzNewHomeState : (fallback.newHomeState ?? ''),
        rentPerWeek: Number(goals.dzRentPerWeek) > 0 ? Number(goals.dzRentPerWeek) : (fallback.rentPerWeek ?? 0),
        annuityShareCapPct: goals.dzAnnuityShareCapPct != null && Number.isFinite(Number(goals.dzAnnuityShareCapPct)) ? Number(goals.dzAnnuityShareCapPct) : DEFAULT_DZ_ANNUITY_SHARE_CAP_PCT,
        annuityAppetite: preference.appetite,
        annuityResidual: preference.residual,
        cashflowTarget: 0,
    };
}

/**
 * Curated downsizer candidate structures (config only). `currentInputs` (manual control) is included for
 * comparison. Each `details` object is consumed by applyDownsizeToFactFind.
 */
export interface DownsizerCandidate {
    id: string;
    name: string;
    description: string;
    details: DownsizeDetails;
    /** Buy a home, or sell and rent — the housing route this candidate takes. */
    housing?: 'buy' | 'rent';
    /** A price sensitivity: listed and explained, never a goal leader (owner, 2 Oct 2026). */
    sensitivity?: boolean;
    // evaluateDownsizerCandidates (calculationApi.ts) takes Record<string, unknown>[] — this index
    // signature lets a typed DownsizerCandidate[] satisfy that without a cast at the call site.
    [key: string]: unknown;
}

/** A price what-if — never ranked, never a card. Decided by the client's reason for moving (DzMotive). */
export const isDzSensitivity = (s: { id?: unknown; sensitivity?: unknown } | null | undefined): boolean =>
    !!s && s.sensitivity === true;

/** Short money for a candidate name — "$1.2m", "$840k" — so a card title stays scannable. */
function formatRough(amount: number): string {
    const n = Math.max(0, Math.round(amount));
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}m`;
    if (n >= 1_000) return `$${Math.round(n / 1_000)}k`;
    return `$${n.toLocaleString('en-AU')}`;
}

/**
 * What could actually be drawn on to top up a dearer purchase.
 *
 * A "buy up" candidate the client cannot fund is not a strategy, so the dearer option is only
 * offered where the money exists. Deliberately excludes the home itself, which is being sold.
 */
function accessibleLiquidFor(factFind: DraftFactFind): number {
    const assets = factFind.assets || {};
    const sum = (list: { value?: number | string }[] | undefined) =>
        (list || []).reduce((total, item) => total + (parseFloat(String(item?.value)) || 0), 0);
    return sum(assets.bankAccounts) + sum(assets.termDeposits) + sum(assets.shares) + sum(assets.accountBasedPensions);
}

const money = (n: number) => n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });

export function generateDownsizerCandidates(factFind: DraftFactFind, options: BuildSuggestionsOptions = {}): DownsizerCandidate[] {
    const carriedHomeValue = parseFloat(String(factFind?.home?.value ?? '')) || 0;
    // The adviser's OWN sale figures, where entered. The candidates must play with the same numbers
    // the manual inputs use, or the two modes model different sales of the same home.
    const ciSalePrice = parseFloat(String(options.currentInputs?.salePrice ?? '')) || 0;
    const homeValue = ciSalePrice > 0 ? ciSalePrice : carriedHomeValue;
    if (!(homeValue > 0)) return [];
    const ff = factFind as unknown as FactFind;
    const isCouple = factFind?.adviceFor === 'couple';
    const clientAge = factFind?.client?.dob ? calculateAge(factFind.client.dob) : null;
    const partnerAge = isCouple && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null;
    // A downsizer contribution needs a qualifying SALE (55+, ten years' ownership, within 90 days
    // of settlement) — never a purchase, so the rent route offers it too.
    const downsizerEligible = (clientAge != null && clientAge >= 55) || (partnerAge != null && partnerAge >= 55);
    const ciSellingCosts = parseFloat(String(options.currentInputs?.sellingCosts ?? ''));
    const sellingCosts = Number.isFinite(ciSellingCosts) && ciSellingCosts >= 0 ? Math.round(ciSellingCosts) : Math.round(homeValue * 0.025);

    const ci = options.currentInputs;
    const ciNewHomePrice = parseFloat(String(ci?.newHomePurchasePrice ?? '')) || 0;
    const ciRents = ci?.rentInstead === true;
    const setup: DzEngineSetup = options.setup ?? {};
    const intended = Math.round(Number(setup.newHomePrice) > 0 ? Number(setup.newHomePrice) : (Number(options.intendedNewHomePrice) || 0));
    /*
     * The price everything else is built around: what they told us they intend to buy, else what is
     * already entered in Manual Control, else the old 70% assumption.
     */
    const newHomeDefault = intended > 0
        ? intended
        : ci && ciNewHomePrice > 0 && !ciRents
            ? Math.round(ciNewHomePrice)
            : Math.round(homeValue * 0.7);

    /*
     * THE DUTY ON THE NEW HOME (owner, 23 Sep 2026). A cost of the changeover the candidates never
     * charged. For the price the client named, the figure on their screen — estimated from the state
     * or overwritten from a quote; for the price ladder's other prices, struck from the same state's
     * scale, since a quote for one price says nothing about another. No state, no duty.
     */
    const newHomeState = String((typeof setup.newHomeState === 'string' && setup.newHomeState) || ci?.newHomeState || '');
    const ciStampDuty = parseFloat(String(ci?.stampDuty ?? '')) || 0;
    const dutyFor = (newHome: number): number => calculateStampDuty(newHomeState, newHome) ?? 0;
    const dutyAt = (newHome: number): number => (newHome === newHomeDefault && ciStampDuty > 0 && !ciRents ? ciStampDuty : dutyFor(newHome));
    const netProceeds = Math.max(0, homeValue - sellingCosts);
    const freedBuying = (newHome: number) => Math.max(0, homeValue - sellingCosts - dutyAt(newHome) - newHome);
    /** What has to come from savings when the new home and its duty cost more than the sale nets. */
    const topUpNeeded = (newHome: number) => Math.max(0, newHome + dutyAt(newHome) - netProceeds);

    /*
     * THE EMERGENCY RESERVE (owner, 2 Oct 2026). The village and aged care engines keep
     * max(6 months' expenses, $50,000) in the bank before deploying anything; the downsizer
     * deployed every dollar. The reserve is a HOUSEHOLD floor: what the bank already holds counts
     * towards it, and only the shortfall is held back from the proceeds (it lands in the bank with
     * the rest of the residue — the event banks whatever is not deployed).
     */
    const bankToday = (factFind.assets?.bankAccounts || []).reduce((s, a) => s + (parseFloat(String(a?.value)) || 0), 0);
    const reserveTopUp = Math.max(0, getRecommendedEmergencyFund(factFind) - bankToday);

    const housing: DzHousing = setup.housing === 'rent' || setup.housing === 'both' || setup.housing === 'buy'
        ? setup.housing
        : (ciRents ? 'rent' : 'buy');
    const motive: DzMotive = setup.motive === 'money' || setup.motive === 'home' ? setup.motive : 'both';
    /** A buy-up's top-up, drawn from savings in the usual order with the reserve kept in the bank. */
    const buyUpFundingFor = (topUp: number) => drawFundingMap(factFind, topUp, ['bankAccounts', 'termDeposits', 'shares', 'accountBasedPensions'], getRecommendedEmergencyFund(factFind));
    const fundedOf = (map: Record<string, number>) => Object.values(map).reduce((sum, v) => sum + (Number(v) || 0), 0);
    const rentPerWeek = Math.max(0, Number(setup.rentPerWeek) > 0 ? Number(setup.rentPerWeek) : (Number(ci?.rentPerWeek) || 0));
    const routes: Array<'buy' | 'rent'> = housing === 'both' ? ['buy', 'rent'] : [housing];

    /*
     * The plain-words annuity answers: "no" builds no annuity candidate at all; "yes" may narrow
     * the field to one residual version; "not sure" (the default) shows both at the cap. The
     * SETUP's answer wins, and where it is silent the CLIENT RECORD is read — the same answer
     * every other module reads (annuityPreference.ts).
     */
    const saved = annuityPreferenceOf(ff);
    const appetite: AnnuityAppetite = setup.annuityAppetite === 'none' || setup.annuityAppetite === 'yes' || setup.annuityAppetite === 'unsure' ? setup.annuityAppetite : saved.appetite;
    const residualAnswer: AnnuityResidual = setup.annuityResidual === 'returned' || setup.annuityResidual === 'nil' || setup.annuityResidual === 'guaranteed' || setup.annuityResidual === 'both' ? setup.annuityResidual : saved.residual;
    const preference = { appetite, residual: appetite === 'yes' ? residualAnswer : 'both', answered: true } as const;
    const annuityCapPct = appetite === 'none' ? 0 : dzAnnuityShareCapOf(setup);
    const annuityShapes: Array<{ suffix: string; name: string; type: 'lifetime' | 'guaranteed'; residual: number; words: string }> = [
        ...(allowsResidual(preference, 0) ? [{ suffix: '', name: 'nil residual', type: 'lifetime' as const, residual: 0, words: 'This version pays the most income and leaves nothing of the purchase price to the estate.' }] : []),
        ...(allowsResidual(preference, 1) ? [{ suffix: '-rcv', name: 'capital returned', type: 'lifetime' as const, residual: 100, words: 'The purchase price comes back to the estate on an early death — in full within the first half of life expectancy, less after that. It pays less income, and Centrelink still counts only 60% either way.' }] : []),
        // Only where the client is in, or entering, government-subsidised care: the product is not sold to anyone else.
        ...(allowsGuaranteed(preference, ff) ? [{ suffix: '-guaranteed', name: 'full amount guaranteed', type: 'guaranteed' as const, residual: 100, words: 'A lifetime annuity and a life policy bought together, so the full purchase price comes back to the estate whenever death comes. It pays the least income, and only the annuity half earns the 60% assets-test treatment.' }] : []),
    ];
    const annuityDeployment = (amount: number, shape: typeof annuityShapes[number], rest: number) => ({
        annuity: amount,
        // The guaranteed shape (annuity + life policy) is its own product; the two plain shapes differ only in residual.
        ...(shape.type === 'guaranteed' ? { annuityType: 'guaranteed' as const } : { annuityType: 'lifetime' as const }),
        annuityResidualPercent: shape.residual,
        annuityIncomeOverride: options.annuityIncomeOverride ?? undefined,
        ...(rest > 0 ? { shares: rest } : {}),
    });

    /** The property a budget buys once its stamp duty is paid, with the rent struck on the PRICE (the village's rule). */
    const purchaseState = isStampDutyState(newHomeState) ? newHomeState : undefined;
    const propertyFor = (budget: number) => {
        const bought = purchaseWithinBudget(purchaseState, budget);
        const rent = Math.round(bought.price * (ASSET_CLASS_ASSUMPTIONS.propertyYieldPct / 100));
        return { investmentProperty: bought.price, ipStampDuty: bought.duty, ipRent: rent, ipExpenses: Math.round(rent * (ASSET_CLASS_ASSUMPTIONS.propertyExpenseRatioPct / 100)) };
    };
    const dutyWords = (duty: number) => (purchaseState
        ? ` Stamp duty of ${money(duty)} (${purchaseState}) comes out of the proceeds, so the property is sized to what is left.`
        : ' Stamp duty on the property is not modelled — no state was chosen.');

    const superRoom = nonConcessionalCapacityForHousehold(ff);
    const whoFirst = (preferred: 'client' | 'partner' | null) =>
        preferred ? ` — ${preferred === 'client' ? (factFind?.client?.firstName || 'the client') : (factFind?.partner?.firstName || 'the partner')} first, whose accumulation super is not counted by Centrelink until Age Pension age` : '';
    const bondBlurb = 'An investment bond pays its own tax inside (30%) and nothing on your tax return; after ten years withdrawals are tax-free. Centrelink counts it in full and deems it, like shares, so this is about tax, not the pension — and the money is best left alone for ten years.';

    const candidates: DownsizerCandidate[] = [];

    /*
     * 0. STAY PUT — the do-nothing benchmark, and the floor every strategy must beat.
     *
     * Without it the engine ranked actions only against each other: whichever downsize was least bad
     * won "best estate", even when not downsizing beat all of them (owner-caught, 2026-08-26). It is
     * excluded from the ranked universe and passed as the benchmark instead — the same discipline
     * the Retirement Village engine uses with its stay-home candidate.
     */
    candidates.push({
        id: 'dz-stay-put',
        name: 'Stay in your current home',
        description: 'No downsize — life as it is today. This is the baseline every strategy below is measured against.',
        details: {},
        stayPut: true,
    });

    routes.forEach((route) => {
        const rents = route === 'rent';
        const p = rents ? 'dz-rent' : 'dz';
        const base: DownsizeDetails = rents
            ? { salePrice: homeValue, sellingCosts, newHomePurchasePrice: 0, rentInstead: true, rentPerWeek }
            : { salePrice: homeValue, sellingCosts, newHomePurchasePrice: newHomeDefault, stampDuty: dutyAt(newHomeDefault), newHomeState };
        const f = rents ? netProceeds : freedBuying(newHomeDefault);
        /** What may be deployed once the reserve is in the bank. */
        const D = Math.max(0, f - reserveTopUp);
        const lead = rents ? 'Sell and rent' : 'Downsize';
        const housingWords = rents
            ? `Sell, and rent instead of buying${rentPerWeek > 0 ? ` (about ${money(rentPerWeek)} a week)` : ' (no rent entered — none is charged)'}. With no home, every dollar is counted and deemed, the higher non-homeowner limit applies, and Rent Assistance may be paid on the rent.`
            : `Buy a home around ${money(newHomeDefault)}.`;
        const reserveWords = reserveTopUp > 0 ? ` ${money(reserveTopUp)} stays in the bank as the emergency reserve.` : '';
        const push = (c: DownsizerCandidate) => candidates.push({ ...c, housing: route });

        // 1. Freed cash to the bank (no super top-up). The simple version every leg is measured against.
        push({
            id: `${p}-simple`,
            name: `${lead} and keep the cash accessible`,
            description: `${housingWords} Keep the freed-up money in the bank. Simple and flexible — but more is counted for the Age Pension.`,
            details: { ...base },
        });

        // 2. SUPER — the downsizer contribution first (a sale is the condition, so renters too).
        let dzUsed = { client: 0, partner: 0, preferred: null as 'client' | 'partner' | null };
        if (downsizerEligible && D > 0) {
            // Age-aware: the member under Age Pension age is filled first, because only their
            // accumulation super escapes the means tests. See allocateDownsizerContribution.
            dzUsed = allocateDownsizerContribution(ff, D);
            const dzTotal = dzUsed.client + dzUsed.partner;
            if (dzTotal > 0) {
                const dzDetails = { ...base, downsizerContribution: dzUsed.client, partnerDownsizerContribution: dzUsed.partner };
                push({
                    id: `${p}-super`,
                    name: `${lead} and top up super (downsizer)`,
                    description: `${housingWords} Move ${money(dzTotal)} of the freed-up money into super using the downsizer contribution (up to $300,000 each)${whoFirst(dzUsed.preferred)}. Often more tax-effective; the pension effect depends on your age and balances. The rest stays in the bank.${reserveWords}`,
                    details: dzDetails,
                });
                // The same contribution commenced as an account-based pension (earnings tax-free; the
                // minimum pension drawn each year) — where the person is old enough to start one.
                const canCommence = (dzUsed.client > 0 && canCommenceAbpFromDownsizer(ff, 'client')) || (dzUsed.partner > 0 && canCommenceAbpFromDownsizer(ff, 'partner'));
                if (canCommence) {
                    push({
                        id: `${p}-super-abp`,
                        name: `${lead}, top up super (downsizer), and start a pension from it`,
                        description: `${housingWords} The same ${money(dzTotal)} downsizer contribution, commenced as an account-based pension: earnings are tax-free and a minimum is drawn each year, but the money is counted by Centrelink from day one${dzUsed.preferred ? ' — including the member whose accumulation super would not have been' : ''}.${reserveWords}`,
                        details: { ...dzDetails, commenceIncomeStream: true },
                    });
                }
                const remainder = Math.max(0, D - dzTotal);
                if (remainder >= DZ_SETUP_SPEND_THRESHOLD) {
                    push({
                        id: `${p}-super-invest`,
                        name: `${lead}, top up super (downsizer), and invest the rest`,
                        description: `${housingWords} The ${money(dzTotal)} downsizer contribution first, then the remaining ${money(remainder)} into a diversified share / managed-fund portfolio rather than the bank.${reserveWords}`,
                        details: { ...dzDetails, deployment: { shares: remainder } },
                    });
                    // Downsizer to the cap + a lifetime annuity with part of the remainder, capped at the
                    // setup's share of the whole deployable amount, the rest into shares.
                    const share = Math.min(remainder, Math.round(D * (annuityCapPct / 100)));
                    if (share >= DZ_ANNUITY_MIN) {
                        annuityShapes.forEach((shape) => push({
                            id: `${p}-super-annuity${shape.suffix}`,
                            name: `${lead}, top up super (downsizer), and buy a lifetime annuity (${shape.name})`,
                            description: `${housingWords} The ${money(dzTotal)} downsizer contribution first; then ${money(share)} into a lifetime annuity for a guaranteed income for life, with only 60% of its price counted for the assets test; the remaining ${money(remainder - share)} into shares. ${shape.words}${reserveWords}`,
                            details: { ...dzDetails, deployment: annuityDeployment(share, shape, remainder - share) },
                        }));
                    }
                }
            }
        }

        // 3–7. Deploy the freed-up money: shares, an investment property, a lifetime annuity, the
        //      means-test fixes, after-tax super, an investment bond.
        if (D >= DZ_SETUP_SPEND_THRESHOLD) {
            push({
                id: `${p}-invest`,
                name: `${lead} and invest the proceeds`,
                description: `${housingWords} Put the freed-up ${money(D)} into a diversified share / managed-fund portfolio instead of leaving it in the bank. Aims for higher long-term growth and income; it's still counted for the pension (deemed), and returns aren't guaranteed.${reserveWords}`,
                details: { ...base, deployment: { shares: D } },
            });

            const ip = propertyFor(D);
            if (ip.investmentProperty > 0) {
                push({
                    id: `${p}-invest-property`,
                    name: `${lead} and buy an investment property`,
                    description: `${housingWords} Use the freed-up money to buy an investment property for rental income and potential growth. The property is an assessable asset and the net rent counts for the pension; it's less flexible than shares and has buying/selling costs.${dutyWords(ip.ipStampDuty)}${reserveWords}`,
                    details: { ...base, deployment: ip },
                });
            }

            /*
             * RESIDUAL CAPITAL IS A CHOICE, AND OFFERING ONLY NIL DISQUALIFIES THE STRATEGY.
             *
             * Centrelink assesses a lifetime annuity at 60% of its purchase price WHATEVER its
             * residual capital value — RCV changes the payment rate, not the assets-test treatment.
             * But net worth values an annuity at its RESIDUAL (assessment/netWorth.ts, deliberately:
             * a nil-RCV annuity really does leave nothing behind), so a nil-RCV candidate loses the
             * legacy ranking by its full purchase price on day one. Both ends are offered so the
             * ranking decides. And NEVER all of it: the setup's share, the rest into shares.
             */
            const share = Math.round(D * (annuityCapPct / 100));
            if (share >= DZ_ANNUITY_MIN) {
                annuityShapes.forEach((shape) => push({
                    id: `${p}-annuity${shape.suffix}`,
                    name: `${lead} and buy a lifetime annuity (${shape.name})`,
                    description: `${housingWords} Convert ${money(share)} of the freed-up money into a lifetime annuity — guaranteed income for life, with only 60% of the purchase price counted for the assets test (for the first five years, or until age 85 if that is later, and 30% after that), which often lifts the Age Pension; the remaining ${money(D - share)} into shares. ${shape.words}${reserveWords}`,
                    details: { ...base, deployment: annuityDeployment(share, shape, D - share) },
                }));
            }

            /*
             * THE TWO LEVERS THAT ACT DIRECTLY ON THE DOWNSIZING TRAP. The home is exempt, the money it
             * turns into is not; a funeral bond and a gift are the two cheapest ways to put part of that
             * money back outside the assessment. Both stay within their limits: the published funeral
             * bond cap per person, and the yearly gifting free area — above it the excess is still
             * counted, and still deemed, for five years, a cost the engine cannot weigh.
             */
            const bondAmount = Math.min(D, FUNERAL_BOND_LIMIT * (isCouple ? 2 : 1));
            if (bondAmount > 0) {
                push({
                    id: `${p}-funeral-bond`,
                    name: `${lead} and shelter part of it in a funeral bond`,
                    description: `${housingWords} Put ${formatCurrency(bondAmount)} of the freed-up money into a funeral bond${isCouple ? 's (one each)' : ''}. A funeral bond is exempt from the assets test up to ${formatCurrency(FUNERAL_BOND_LIMIT)} per person, so that part stops being counted — the simplest way to soften the downsizing trap, on money most families set aside anyway. The rest goes into shares.${reserveWords}`,
                    details: { ...base, funeralBondPurchase: bondAmount, deployment: { shares: Math.max(0, D - bondAmount) } },
                });
            }
            const giftAmount = Math.min(D, GIFTING_LIMIT_ANNUAL);
            if (giftAmount > 0) {
                push({
                    id: `${p}-gift`,
                    name: `${lead} and gift within the free area`,
                    description: `${housingWords} Gift ${formatCurrency(giftAmount)} of the freed-up money to family. Gifts up to ${formatCurrency(GIFTING_LIMIT_ANNUAL)} a year leave the means test straight away; anything above that is still counted, and still deemed to earn income, for five years — so this stays inside the free area. The rest goes into shares.${reserveWords}`,
                    details: { ...base, giftingAmount: giftAmount, deployment: { shares: Math.max(0, D - giftAmount) } },
                });
            }

            /*
             * AFTER-TAX SUPER (F.6) AND INVESTMENT BONDS (F.5) — 17 Sep 2026. A person under 75 with
             * room under the total super balance bands may contribute after tax — up to $390,000 each
             * with the bring-forward. Where the super door is shut (75+, or the bands full) the
             * tax-paid alternative is an investment bond. Logic-based: each candidate exists only
             * where the rules would let the person make the move, capped at what they may contribute.
             */
            const afterDownsizer = Math.max(0, D - dzUsed.client - dzUsed.partner);
            if (superRoom.total > 0) {
                const ncc = allocateNonConcessionalContribution(ff, D);
                const nccTotal = ncc.client + ncc.partner;
                if (nccTotal >= 25_000) {
                    push({
                        id: `${p}-super-after-tax`,
                        name: `${lead} and contribute to super after tax`,
                        description: `${housingWords} Put ${formatCurrency(nccTotal)} of the freed-up money into super as an after-tax (non-concessional) contribution${whoFirst(ncc.preferred)}. Earnings are taxed at super rates rather than your own. Capped at what each of you may contribute now${superRoom.client.balanceEstimated || superRoom.partner.balanceEstimated ? ' (worked out from the super balances recorded)' : ''}; anything above the cap goes into shares.${reserveWords}`,
                        details: { ...base, deployment: { superContribution: ncc.client, partnerSuperContribution: ncc.partner, superCommenceIncomeStream: ncc.preferred == null, shares: Math.max(0, D - nccTotal) } },
                    });
                }
                if (downsizerEligible && afterDownsizer >= 25_000 && (dzUsed.client + dzUsed.partner) > 0) {
                    const more = allocateNonConcessionalContribution(ff, afterDownsizer);
                    const moreTotal = more.client + more.partner;
                    if (moreTotal >= 25_000) {
                        push({
                            id: `${p}-super-max`,
                            name: `${lead} and put the most you can into super`,
                            description: `${housingWords} The downsizer contribution first (${formatCurrency(dzUsed.client + dzUsed.partner)}), then ${formatCurrency(moreTotal)} more as an after-tax contribution${whoFirst(more.preferred)} — the most the rules let you move into super from this sale. Anything left goes into shares.${reserveWords}`,
                            details: { ...base, downsizerContribution: dzUsed.client, partnerDownsizerContribution: dzUsed.partner, deployment: { superContribution: more.client, partnerSuperContribution: more.partner, superCommenceIncomeStream: more.preferred == null, shares: Math.max(0, afterDownsizer - moreTotal) } },
                        });
                    }
                }
            }
            if (superRoom.total === 0) {
                push({
                    id: `${p}-invest-bond`,
                    name: `${lead} and put the proceeds into an investment bond`,
                    description: `${housingWords} Put the freed-up ${formatCurrency(D)} into an investment bond — the tax-paid home for money super can no longer take. ${bondBlurb}${reserveWords}`,
                    details: { ...base, deployment: { investmentBond: D } },
                });
            } else if (D - superRoom.total >= DZ_SETUP_SPEND_THRESHOLD) {
                const ncc = allocateNonConcessionalContribution(ff, D);
                const toBond = Math.max(0, D - ncc.client - ncc.partner);
                push({
                    id: `${p}-super-and-bond`,
                    name: `${lead}, fill super after tax, and bond the rest`,
                    description: `${housingWords} Put ${formatCurrency(ncc.client + ncc.partner)} into super as an after-tax contribution${whoFirst(ncc.preferred)} — the most the caps allow — and the remaining ${formatCurrency(toBond)} into an investment bond. ${bondBlurb}${reserveWords}`,
                    details: { ...base, deployment: { superContribution: ncc.client, partnerSuperContribution: ncc.partner, superCommenceIncomeStream: ncc.preferred == null, investmentBond: toBond } },
                });
            }
        }
    });

    /*
     * THE PRICE LADDER — a sensitivity or a strategy, by the client's REASON for moving (owner,
     * 3 Oct 2026). Same sale, same deployment (cash), only the price differs, so the reader sees
     * what the PRICE decision does: cheaper frees capital that is then counted (the trap); similar
     * frees almost nothing; dearer moves counted savings INTO the exempt home and can LIFT the
     * pension — the opposite of the trap.
     *
     *   'money'  the client wants the money out: every price is a what-if, listed and explained,
     *            never crowned — a dearer home would win "Best Age Pension" by hiding more, which
     *            is true and is not what they asked for (owner, 2 Oct 2026).
     *   'home'   the client wants a home that suits them better: the similar and dearer homes —
     *            and the most home the savings allow — are real candidates that compete for the
     *            goals; the cashflow-covered rule says whether the bills are still paid. The
     *            cheaper move stays a what-if.
     *   'both'   compare everything.
     *
     * A buy-up's top-up is drawn from savings in the usual order with the reserve kept, and
     * carried as `buyUpFunding` so the event sells the right assets rather than overdrawing the
     * bank; a buy-up the savings cannot fund is not generated.
     */
    if (routes.includes('buy')) {
        const ladder: Array<{ id: string; price: number; label: string; note: string; sensitivity: boolean; buyUpFunding?: Record<string, number> }> = [];
        const cheaper = Math.round(homeValue * 0.6);
        if (cheaper > 0 && Math.abs(cheaper - newHomeDefault) > homeValue * 0.05) {
            ladder.push({ id: 'dz-price-cheaper', price: cheaper, label: `Buy a cheaper home (${formatRough(cheaper)})`, note: `Frees up the most — ${formatRough(freedBuying(cheaper))} after costs. It is also the version that counts the most against the Age Pension, because the home was not counted and the cash is.`, sensitivity: motive !== 'both' });
        }
        const similar = Math.round(netProceeds);
        if (similar > 0 && Math.abs(similar - newHomeDefault) > homeValue * 0.05) {
            ladder.push({ id: 'dz-price-similar', price: similar, label: `Buy a home at a similar price (${formatRough(similar)})`, note: 'Spends the whole sale on the new home. Nothing is freed up and nothing new is counted — the move is about the house, not the money, and the selling costs are what it costs you.', sensitivity: motive === 'money' });
        }
        const dearer = Math.round(homeValue * 1.15);
        const topUp = topUpNeeded(dearer);
        if (topUp > 0 && Math.abs(dearer - newHomeDefault) > homeValue * 0.05) {
            const funding = buyUpFundingFor(topUp);
            if (fundedOf(funding) >= topUp - 1) {
                ladder.push({ id: 'dz-price-dearer', price: dearer, label: `Buy a somewhat dearer home (${formatRough(dearer)})`, note: `Costs ${formatRough(topUp)} more than the sale nets, topped up from savings. A home is not counted by Centrelink, so that money stops being counted and the Age Pension can rise rather than fall — the opposite of the usual downsizing trap. The trade-off: that money is in the house now, not available to spend.`, sensitivity: motive === 'money', buyUpFunding: funding });
            }
        }
        /*
         * THE MOST HOME THE SAVINGS ALLOW — only where the reason is the home. Every spare dollar
         * above the reserve goes into the exempt home: the largest lift the assets test can give,
         * and nothing left invested, so the cashflow rests on the pension and any other income.
         * Offered only where it is materially above the dearer rung.
         */
        if (motive !== 'money') {
            const spare = Math.max(0, accessibleLiquidFor(factFind) - getRecommendedEmergencyFund(factFind));
            const most = purchaseWithinBudget(purchaseState, netProceeds + spare).price;
            const mostTopUp = topUpNeeded(most);
            // Materially above the anchor and above the dearer rung WHERE THAT RUNG EXISTS — a
            // client who cannot fund +15% (owner-caught 3 Oct 2026: $780k liquid against an $800k
            // top-up) still has a buy-up the savings DO allow, and it is the one that lifts the pension.
            const floor = Math.max(newHomeDefault, ladder.some((r) => r.id === 'dz-price-dearer') ? dearer : 0);
            if (spare > 0 && most > floor * 1.05 && mostTopUp > 0) {
                const funding = buyUpFundingFor(mostTopUp);
                if (fundedOf(funding) >= mostTopUp - 1) {
                    ladder.push({ id: 'dz-price-max', price: most, label: `Buy a dearer home, using the savings (${formatRough(most)})`, note: `Puts ${formatRough(mostTopUp)} of savings — everything above the ${formatRough(getRecommendedEmergencyFund(factFind))} kept in reserve — towards a dearer home. A home is not counted by Centrelink, so less is counted and the Age Pension can rise. The trade-off: almost nothing is left invested, so the bills rest on the pension and any other income.`, sensitivity: false, buyUpFunding: funding });
                }
            }
        }
        ladder.forEach(({ id, price, label, note, sensitivity, buyUpFunding }) => {
            candidates.push({
                id,
                name: label,
                description: `${note} Everything else is held the same, so this shows what the price alone does.`,
                details: { salePrice: homeValue, sellingCosts, newHomePurchasePrice: price, stampDuty: dutyAt(price), newHomeState, ...(buyUpFunding ? { buyUpFunding } : {}) },
                housing: 'buy',
                sensitivity,
            });
        });
    }

    // The client's current plan (if they've entered a downsize) — for comparison.
    if (ci && ciNewHomePrice >= 0 && ((parseFloat(String(ci.salePrice ?? '')) || 0) > 0 || homeValue > 0)) {
        candidates.push({
            id: 'dz-your-plan',
            name: 'Your current plan',
            description: 'The downsize you entered in Manual Control, for comparison.',
            housing: ciRents ? 'rent' : 'buy',
            details: {
                salePrice: parseFloat(String(ci.salePrice ?? '')) || homeValue,
                sellingCosts: parseFloat(String(ci.sellingCosts ?? '')) || sellingCosts,
                newHomePurchasePrice: ciRents ? 0 : ciNewHomePrice,
                // The client's own plan carries the figure on their screen, whatever it was.
                stampDuty: ciRents ? 0 : ciStampDuty,
                newHomeState,
                rentInstead: ciRents,
                rentPerWeek: ciRents ? (Number(ci.rentPerWeek) || 0) : 0,
                downsizerContribution: parseFloat(String(ci.downsizerContribution ?? '')) || 0,
                partnerDownsizerContribution: parseFloat(String(ci.partnerDownsizerContribution ?? '')) || 0,
                commenceIncomeStream: !!ci.commenceIncomeStream,
                giftingAmount: parseFloat(String(ci.giftingAmount ?? '')) || 0,
                funeralBondPurchase: parseFloat(String(ci.funeralBondPurchase ?? '')) || 0,
                deployment: ci.deployment || {},
            },
        });
    }

    /*
     * DEBT CLEARANCE — paired against every downsizing candidate above (never the benchmark, never
     * a sensitivity). The interest stops and the repayments leave the cashflow, but the money spent
     * is money no other part of the plan can use — the competition worth ranking. Below $5,000 a
     * payoff cannot move a ranking and each twin costs a full projection — the same materiality
     * floor the aged care and village engines use.
     */
    const debts = payableDebtsOf(factFind);
    const totalDebt = debts.reduce((sum, d) => sum + d.balance, 0);
    if (totalDebt >= 5_000) {
        const payments = Object.fromEntries(debts.map((d) => [d.key, d.balance]));
        const twins = candidates
            .filter((c) => !c.stayPut && !c.sensitivity)
            .map((c) => ({
                ...c,
                id: `${c.id}-clear-debt`,
                name: `${c.name}, and clear your debts`,
                description: `${c.description} This version also clears your outstanding debts of ${formatCurrency(totalDebt)} at the same time — the interest stops and the repayments leave your cashflow, but that money is no longer available for anything else in the plan.`,
                debtPayoff: { payments, repaymentBasis: 'keep_repayment' as const },
            }));
        candidates.push(...twins);
    }

    return candidates;
}

export interface DownsizerSuggestionsResult {
    strategies: EvaluatedStrategy[];
    recommended: RecommendedStrategyEntry[];
    /** The do-nothing benchmark, for the panel and the plan summary to measure against. */
    stayPut?: EvaluatedStrategy | null;
    /**
     * Everything a SINGLE candidate needs to be evaluated again, exactly as it was the first time.
     *
     * The deep dive's financial/calendar toggle re-cuts the candidate it is showing, and the only
     * faithful way is to hand the engine the same candidate, the same horizon and the same date
     * (owner, 23 Sep 2026). Carried whole rather than rebuilt at the call site: the evaluated row
     * does not keep everything a candidate was built with (a debt-payoff plan, for one), and the
     * date is derived inside this builder, so a second derivation there would be free to disagree.
     */
    rerun: { candidates: unknown[]; legacyYears: number; date?: string };
}

/**
 * Evaluate every candidate and rank into goal leaders.
 */
export async function buildDownsizerSuggestions(factFind: DraftFactFind, options: BuildSuggestionsOptions = {}): Promise<DownsizerSuggestionsResult> {
    const legacyYears = options.legacyYears || 10;
    const candidates = generateDownsizerCandidates(factFind, options);

    // One request evaluates every candidate against the engine server-side (see
    // server/src/rest/calculation/suggestionEngine/downsizer.ts) instead of one request per candidate.
    // The case's downsize date rides along so every candidate is projected on the same timing as the
    // manual run — timing belongs to the case, never to a strategy.
    const strategies: EvaluatedStrategy[] = await calculationApi.evaluateDownsizerCandidates(factFind, candidates, legacyYears, options.downsizeDate);

    // Ranked among the MOVE strategies, each measured against staying put — so nothing is ever
    // recommended that leaves the client worse off than doing nothing. The price ladder is a
    // sensitivity: evaluated and listed, never ranked.
    const stayPut = strategies.find((s) => s.id === 'dz-stay-put') || null;
    const moveStrategies = strategies.filter((s) => s.id !== 'dz-stay-put' && !isDzSensitivity(s as { id?: unknown }));
    const recommended = buildRecommendedOptimalStrategies(moveStrategies, stayPut, { cashflowTarget: Number(options.setup?.cashflowTarget) || 0 });
    return { strategies, recommended, stayPut, rerun: { candidates, legacyYears, date: options.downsizeDate } };
}
